import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Plus, Trash2, FileText, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../store/useStore';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../lib/exportUtils';
import { normalizePriceListItems } from '../lib/priceListCustomLines';
import {
  computeDepositBase,
  createBalanceInvoiceFromPriceList,
  createDepositInvoiceFromPriceList,
  createFullInvoiceFromPriceList,
  validateInstallments,
  type InstallmentInput,
} from '../lib/priceListInvoiceUtils';
import { COMPANY_INFO } from '../lib/letterheadPdf';
import type { Database } from '../types/database.types';

type InvoiceRow = Database['public']['Tables']['invoices']['Row'];

type Mode = 'full' | 'deposit' | 'balance';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  priceListId: string;
  onCreated?: (invoice: InvoiceRow) => void;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

const defaultDepositPlan: InstallmentInput[] = [
  { percentage: 100, due_days: 0, label: 'a vista' },
];

const defaultBalancePlan: InstallmentInput[] = [
  { percentage: 30, due_days: 0, label: 'a vista' },
  { percentage: 70, due_days: 30, label: '30 gg DF' },
];

const defaultFullPlan: InstallmentInput[] = [
  { percentage: 100, due_days: 0, label: 'a vista' },
];

const planForMode = (m: Mode): InstallmentInput[] =>
  m === 'deposit' ? defaultDepositPlan : m === 'balance' ? defaultBalancePlan : defaultFullPlan;

export const CreateInvoiceFromOfferModal: React.FC<Props> = ({
  isOpen,
  onClose,
  priceListId,
  onCreated,
}) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [priceList, setPriceList] = useState<any | null>(null);
  const [existingDeposits, setExistingDeposits] = useState<InvoiceRow[]>([]);

  const [mode, setMode] = useState<Mode>('full');
  const [depositPct, setDepositPct] = useState<number>(30);
  const [invoiceDate, setInvoiceDate] = useState<string>(todayISO());
  const [parentInvoiceId, setParentInvoiceId] = useState<string>('');
  const [installments, setInstallments] = useState<InstallmentInput[]>(defaultFullPlan);
  const [issuedStatus, setIssuedStatus] = useState<'draft' | 'issued'>('issued');
  const [includeCanone, setIncludeCanone] = useState<boolean>(true);

  useEffect(() => {
    if (!isOpen) return;
    setMode('full');
    setDepositPct(30);
    setInvoiceDate(todayISO());
    setParentInvoiceId('');
    setInstallments(defaultFullPlan);
    setIssuedStatus('issued');
    setIncludeCanone(true);
    loadPriceList();
  }, [isOpen, priceListId]);

  useEffect(() => {
    setInstallments(planForMode(mode));
    if (mode === 'balance' && !parentInvoiceId && existingDeposits.length > 0) {
      setParentInvoiceId(existingDeposits[0].id);
    }
  }, [mode, existingDeposits.length]);

  const loadPriceList = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('price_lists')
        .select(`*, price_list_items (*, products (*))`)
        .eq('id', priceListId)
        .single();
      if (error) throw error;
      // Righe manuali (product_id null): prodotto sintetico dai campi custom_*
      setPriceList({
        ...(data as any),
        price_list_items: normalizePriceListItems((data as any).price_list_items),
      });

      const { data: deposits } = await supabase
        .from('invoices')
        .select('*')
        .eq('price_list_id', priceListId)
        .not('deposit_percentage', 'is', null)
        .order('created_at', { ascending: false });
      setExistingDeposits((deposits ?? []) as InvoiceRow[]);
    } catch (e: any) {
      addNotification({
        type: 'error',
        title: 'Errore',
        message: e.message || 'Impossibile caricare l\'offerta',
      });
    } finally {
      setLoading(false);
    }
  };

  const breakdown = useMemo(() => {
    if (!priceList) return { products: 0, canone: 0, base: 0 };
    return computeDepositBase(priceList, { includeCanone });
  }, [priceList, includeCanone]);

  const depositAmount = useMemo(
    () => Math.round(breakdown.base * depositPct) / 100,
    [breakdown.base, depositPct]
  );

  const parent = useMemo(
    () => existingDeposits.find((d) => d.id === parentInvoiceId),
    [existingDeposits, parentInvoiceId]
  );

  const offerGrossFull = useMemo(() => {
    if (!priceList) return 0;
    const items: any[] = priceList.price_list_items || [];
    const canoneMonths: number = priceList.canone_months ?? 1;
    const noVat = !!priceList.no_vat_forfettario;
    let total = 0;
    for (const it of items) {
      const cat = (it.products?.category || '').toLowerCase();
      if (cat === 'a consumo') continue;
      const qty = (it.min_quantity ?? 1) * (cat === 'canone' ? canoneMonths : 1);
      const taxable = it.price * qty * (1 - (it.discount_percentage ?? 0) / 100);
      const vat = noVat ? 0 : taxable * (it.products?.iva ?? 22) / 100;
      total += taxable + vat;
    }
    return Math.round(total * 100) / 100;
  }, [priceList]);

  const balanceGross = useMemo(() => {
    if (!parent) return 0;
    return Math.round((offerGrossFull - Number(parent.total_amount)) * 100) / 100;
  }, [offerGrossFull, parent]);

  const targetTotal =
    mode === 'deposit'
      ? depositAmount * (priceList?.no_vat_forfettario ? 1 : 1.22)
      : mode === 'balance'
        ? balanceGross
        : offerGrossFull;

  const installmentError = validateInstallments(installments);

  const installmentRows = useMemo(() => {
    return installments.map((r, idx) => {
      const amount = Math.round((targetTotal * r.percentage) / 100 * 100) / 100;
      const due = new Date(invoiceDate);
      due.setDate(due.getDate() + r.due_days);
      return {
        idx,
        amount,
        due_date: due.toLocaleDateString('it-IT'),
        ...r,
      };
    });
  }, [installments, targetTotal, invoiceDate]);

  const updateInstallment = (idx: number, patch: Partial<InstallmentInput>) => {
    setInstallments((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addInstallment = () => {
    setInstallments((prev) => [...prev, { percentage: 0, due_days: 30, label: '' }]);
  };
  const removeInstallment = (idx: number) => {
    setInstallments((prev) => prev.filter((_, i) => i !== idx));
  };

  const canSubmit = useMemo(() => {
    if (!user?.id) return false;
    if (!priceList) return false;
    if (priceList.status !== 'accepted' && priceList.status !== 'partially_invoiced' && existingDeposits.length === 0) return false;
    if (mode === 'deposit') {
      if (depositPct <= 0 || depositPct >= 100) return false;
      if (breakdown.base <= 0) return false;
    } else if (mode === 'balance') {
      if (!parentInvoiceId) return false;
      if (balanceGross <= 0) return false;
    } else {
      if (offerGrossFull <= 0) return false;
    }
    if (installmentError) return false;
    return true;
  }, [user, priceList, mode, depositPct, breakdown.base, parentInvoiceId, balanceGross, offerGrossFull, installmentError, existingDeposits.length]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      let invoice: InvoiceRow;
      if (mode === 'deposit') {
        invoice = await createDepositInvoiceFromPriceList({
          priceListId,
          depositPercentage: depositPct,
          invoiceDate,
          installments,
          status: issuedStatus,
          iban: COMPANY_INFO.iban,
          includeCanone,
        });
      } else if (mode === 'balance') {
        invoice = await createBalanceInvoiceFromPriceList({
          priceListId,
          parentInvoiceId,
          invoiceDate,
          installments,
          status: issuedStatus,
          iban: COMPANY_INFO.iban,
        });
      } else {
        invoice = await createFullInvoiceFromPriceList({
          priceListId,
          invoiceDate,
          installments,
          status: issuedStatus,
          iban: COMPANY_INFO.iban,
        });
      }
      const modeLabel = mode === 'deposit' ? 'Acconto' : mode === 'balance' ? 'Saldo' : 'Fattura';
      addNotification({
        type: 'success',
        title: 'Fattura creata',
        message: `${modeLabel} ${invoice.invoice_number ?? '(bozza)'} creato.`,
      });
      onCreated?.(invoice);
      onClose();
    } catch (e: any) {
      addNotification({
        type: 'error',
        title: 'Errore creazione fattura',
        message: e.message || 'Errore sconosciuto',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Crea Fattura da Offerta
          </DialogTitle>
          <DialogDescription>
            {priceList?.name ? `Offerta: ${priceList.name}` : 'Caricamento...'}
          </DialogDescription>
        </DialogHeader>

        {loading || !priceList ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Stato offerta warning */}
            {priceList.status !== 'accepted' && priceList.status !== 'partially_invoiced' && existingDeposits.length === 0 && (
              <div className="flex items-start gap-2 p-3 rounded border border-amber-300 bg-amber-50 text-amber-900 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                <span>
                  Offerta in stato <strong>{priceList.status}</strong>. Solo le offerte
                  <strong> accettate</strong> possono essere fatturate.
                </span>
              </div>
            )}

            {/* Riepilogo */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm bg-gray-50 rounded p-3 border">
              <div>
                <div className="text-xs text-gray-500">Prodotti</div>
                <div className="font-mono font-semibold">{formatCurrency(breakdown.products)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Canone ({priceList.canone_months ?? 1} mesi)</div>
                <div className="font-mono font-semibold">{formatCurrency(breakdown.canone)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Base imponibile</div>
                <div className="font-mono font-bold text-green-700">{formatCurrency(breakdown.base)}</div>
              </div>
              <div className="sm:col-span-3 text-xs text-gray-500">
                Voci "a consumo" escluse (fatturate separatamente). Regime forfettario: {priceList.no_vat_forfettario ? 'sì (no IVA)' : 'no (IVA 22%)'}.
              </div>
            </div>

            {/* Tipo fattura */}
            <div className="space-y-2">
              <Label>Tipo fattura</Label>
              <div className="grid grid-cols-3 gap-2 sm:flex sm:gap-2">
                <Button
                  type="button"
                  variant={mode === 'full' ? 'default' : 'outline'}
                  onClick={() => setMode('full')}
                  size="sm"
                  disabled={existingDeposits.length > 0}
                  title={existingDeposits.length > 0 ? 'Esiste già un acconto: usa Saldo' : ''}
                >
                  Completa
                </Button>
                <Button
                  type="button"
                  variant={mode === 'deposit' ? 'default' : 'outline'}
                  onClick={() => setMode('deposit')}
                  size="sm"
                >
                  Acconto
                </Button>
                <Button
                  type="button"
                  variant={mode === 'balance' ? 'default' : 'outline'}
                  onClick={() => setMode('balance')}
                  size="sm"
                  disabled={existingDeposits.length === 0}
                  title={existingDeposits.length === 0 ? 'Nessuna fattura acconto esistente' : ''}
                >
                  Saldo
                </Button>
              </div>
            </div>

            {/* Deposit pct */}
            {mode === 'deposit' && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="depositPct">Percentuale acconto</Label>
                    <div className="flex items-center gap-1">
                      <Input
                        id="depositPct"
                        type="number"
                        min={1}
                        max={99}
                        step={1}
                        value={depositPct}
                        onChange={(e) => setDepositPct(Number(e.target.value))}
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                  </div>
                  <div>
                    <Label>Importo acconto (imponibile)</Label>
                    <div className="font-mono font-bold text-lg pt-2">
                      {formatCurrency(depositAmount)}
                    </div>
                  </div>
                </div>
                <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={includeCanone}
                    onChange={(e) => setIncludeCanone(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300"
                  />
                  <span>
                    <span className="font-medium">Includi canoni mensili nell'acconto</span>
                    <span className="block text-xs text-gray-500">
                      Se attivo: 2 righe in fattura (prodotti + canone × {priceList.canone_months ?? 1} mesi). Se disattivo: solo prodotti, canoni esclusi dall'acconto.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Parent invoice select for balance */}
            {mode === 'balance' && (
              <div>
                <Label htmlFor="parent">Fattura acconto di riferimento</Label>
                <Select value={parentInvoiceId} onValueChange={setParentInvoiceId}>
                  <SelectTrigger id="parent">
                    <SelectValue placeholder="Seleziona acconto..." />
                  </SelectTrigger>
                  <SelectContent>
                    {existingDeposits.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.invoice_number ?? `Bozza ${d.id.slice(0, 8)}`} —{' '}
                        {formatCurrency(Number(d.total_amount))} ({d.deposit_percentage}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {parent && (
                  <div className="text-xs text-gray-600 mt-1">
                    Saldo (lordo): <span className="font-mono">{formatCurrency(balanceGross)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Data fattura + stato */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="date">Data fattura</Label>
                <Input
                  id="date"
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Stato</Label>
                <Select value={issuedStatus} onValueChange={(v) => setIssuedStatus(v as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Bozza (no numero)</SelectItem>
                    <SelectItem value="issued">Emessa (numera)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Piano rate */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Piano pagamenti</Label>
                <Button type="button" variant="outline" size="sm" onClick={addInstallment}>
                  <Plus className="w-3 h-3 mr-1" /> Rata
                </Button>
              </div>
              <div className="table-scroll"><table className="w-full text-sm">
                <thead className="text-xs text-gray-500">
                  <tr>
                    <th className="text-left pb-1">#</th>
                    <th className="text-left pb-1">Etichetta</th>
                    <th className="text-right pb-1">%</th>
                    <th className="text-right pb-1">gg</th>
                    <th className="text-right pb-1">Importo</th>
                    <th className="text-right pb-1">Scadenza</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {installmentRows.map((r) => (
                    <tr key={r.idx} className="border-t">
                      <td className="py-1">{r.idx + 1}</td>
                      <td className="py-1">
                        <Input
                          value={r.label ?? ''}
                          onChange={(e) => updateInstallment(r.idx, { label: e.target.value })}
                          placeholder="a vista, 30gg..."
                          className="h-7 text-xs min-w-[7rem]"
                        />
                      </td>
                      <td className="py-1 w-20">
                        <Input
                          type="number"
                          value={r.percentage}
                          onChange={(e) => updateInstallment(r.idx, { percentage: Number(e.target.value) })}
                          className="h-7 text-xs text-right"
                        />
                      </td>
                      <td className="py-1 w-20">
                        <Input
                          type="number"
                          value={r.due_days}
                          onChange={(e) => updateInstallment(r.idx, { due_days: Number(e.target.value) })}
                          className="h-7 text-xs text-right"
                        />
                      </td>
                      <td className="py-1 text-right font-mono">{formatCurrency(r.amount)}</td>
                      <td className="py-1 text-right text-xs text-gray-600">{r.due_date}</td>
                      <td className="py-1 w-8">
                        {installments.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeInstallment(r.idx)}
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
              {installmentError && (
                <p className="text-xs text-red-600">{installmentError}</p>
              )}
              <div className="text-xs text-gray-600 text-right">
                Totale fattura stimato: <span className="font-mono font-semibold">{formatCurrency(targetTotal)}</span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Annulla
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creazione...
              </>
            ) : mode === 'deposit' ? (
              'Crea Fattura Acconto'
            ) : mode === 'balance' ? (
              'Crea Fattura Saldo'
            ) : (
              'Crea Fattura Completa'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateInvoiceFromOfferModal;
